import type { LoadableComponent } from 'react-loadable';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { ADMINHUB, ADMINHUB_ORG_CREATE } from '@adminhub/feature-registry/feature-registry';
import { PickPageLoader } from '@adminhub/loading/ui/pick-loader';
import { RouteNames } from '@adminhub/router';

// hint: Do not webpackPreload or  webpackPrefetch any page
// why: we have preloader

// hint: add /* webpackMode: "eager" */ to those roots, which shall not be split

// roots
const RequestGate = PickPageLoader(
	async () => import(/* webpackChunkName: "request-gate" */ '@adminhub-page/shadow-it'),
	({ ShadowItRequestGate: Page }) => Page,
);

const OrgPickerSwitcher = PickPageLoader(
	async () =>
		import(/* webpackChunkName: "org-picker-switcher-app" */ '@adminhub-page/onboarding-agp'),
	({ OrgPickerSwitcher: Page }) => Page,
);

const CreateOrganizationPage = PickPageLoader(
	async () => import(/* webpackChunkName: "create-org-app" */ '@adminhub-page/create-page'),
	({ CreateOrganizationPage: Page }) => Page,
);

const Error404Page = PickPageLoader(
	async () =>
		import(
			/* webpackMode: "eager" */ /* webpackChunkName: "error-404-page" */ '@adminhub/error-pages/ui/error-404-page'
		),
	({ Error404Page: Page }) => Page,
);

const OrgRoutes = PickPageLoader(
	async () =>
		import(/* webpackMode: "eager" */ /* webpackChunkName: "org-app" */ '../routes/org-routes'),
	({ OrgRoutes: Page }) => Page,
);

const CreateWithRovoPreviewRoute = PickPageLoader(
	async () =>
		import(
			/* webpackChunkName: "unified-policy-rovo-preview-route" */ '../routes/create-with-rovo-preview-route'
		),
	({ CreateWithRovoPreviewRoute: Page }) => Page,
);

const SiteRoutes = PickPageLoader(
	async () =>
		import(/* webpackMode: "eager" */ /* webpackChunkName: "site-app" */ '../routes/site-routes'),
	({ SiteRoutes: Page }) => Page,
);

const Dashboard = PickPageLoader(
	async () =>
		import(
			/* webpackMode: "eager" */ /* webpackChunkName: "dashboard-app" */ '@adminhub/dashboard'
		),
	({ Dashboard: Page }) => Page,
);

const UbpMetersOrgPicker = PickPageLoader(
	async () =>
		import(
			/* webpackChunkName: "ubp-meters-org-picker" */ '../ubp-meters-org-picker/ubp-meters-org-picker'
		),
	({ UbpMetersOrgPicker: Page }) => Page,
);

const OrgOptIn = PickPageLoader(
	async () => import('@adminhub/migration-shared-experiences-org-opt-in'),
	({ OrgOptIn: Page }) => Page,
);

const DspDeeplink = PickPageLoader(
	async () => import(/* webpackChunkName: "dsp-deeplink" */ '../dsp-deeplink'),
	({ DspDeeplink: Page }) => Page,
);

const DataClassificationDeeplink = PickPageLoader(
	async () =>
		import(
			/* webpackChunkName: "data-classification-deeplink" */ '../data-classification-deeplink'
		),
	({ DataClassificationDeeplink: Page }) => Page,
);

const GuardAlertsDeeplink = PickPageLoader(
	async () => import(/* webpackChunkName: "guard-alerts-deeplink" */ '../guard-alerts-deeplink'),
	({ GuardAlertsDeeplink: Page }) => Page,
);

const DataScanDeeplink = PickPageLoader(
	async () => import(/* webpackChunkName: "data-scan-deeplink" */ '../data-scan-deeplink'),
	({ DataScanDeeplink: Page }) => Page,
);

const GuardUserActivityDeeplink = PickPageLoader(
	async () =>
		import(
			/* webpackChunkName: "guard-user-activity-deeplink" */ '../guard-user-activity-deeplink'
		),
	({ GuardUserActivityDeeplink: Page }) => Page,
);

const GuardContentScanningDeeplink = PickPageLoader(
	async () =>
		import(
			/* webpackChunkName: "guard-content-scanning-deeplink" */ '../guard-content-scanning-deeplink'
		),
	({ GuardContentScanningDeeplink: Page }) => Page,
);

type RootRoutes = Record<
	string,
	{
		exact?: boolean;
		navigation: boolean;
		importer: React.ComponentType<any> & LoadableComponent;
		name?: string;
	}
>;

export const ROOT_ROUTES: RootRoutes = {
	'/meters': {
		importer: UbpMetersOrgPicker,
		navigation: true,
		exact: true,
		name: RouteNames.TOP_ROUTE_UBP_METERS_ORG_PICKER,
	},
	'/product-request-workflow/*': {
		importer: RequestGate,
		navigation: false,
		name: RouteNames.TOP_ROUTE_SHADOW_IT_REQUEST_GATE,
	},
	'/atlassian-access': {
		importer: OrgPickerSwitcher,
		navigation: true,
		name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_ACCESS,
	},
	'/atlassian-guard': {
		importer: OrgPickerSwitcher,
		navigation: true,
		name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_GUARD,
	},
	'/atlassian-guard-premium': {
		importer: OrgPickerSwitcher,
		navigation: true,
		name: RouteNames.TOP_ROUTE_ORG_PICKER_ATLASSIAN_GUARD_PREMIUM,
	},
	// Deeplink feature gate uses fg() inside page components (not here): module-level fg() runs before FeatureGatesInitialization.
	// exact: false so paths still match with query params (e.g. ?bifrost-override= for branch preview).
	'/data-security-policies': {
		importer: DspDeeplink,
		navigation: true,
		exact: false,
	},
	'/data-classification': {
		importer: DataClassificationDeeplink,
		navigation: true,
		exact: false,
	},
	'/guard-alerts': {
		importer: GuardAlertsDeeplink,
		navigation: true,
		exact: false,
	},
	'/data-scan': {
		importer: DataScanDeeplink,
		navigation: true,
		exact: false,
	},
	'/guard-user-activity': {
		importer: GuardUserActivityDeeplink,
		navigation: true,
		exact: false,
	},
	'/guard-content-scanning': {
		importer: GuardContentScanningDeeplink,
		navigation: true,
		exact: false,
	},
	'/o/create': !isFeatureEnabled(ADMINHUB, ADMINHUB_ORG_CREATE)
		? {
				importer: Error404Page,
				navigation: true,
			}
		: {
				importer: CreateOrganizationPage,
				navigation: false,
			},
	'/o/:orgId/policies/create-with-rovo-preview': {
		importer: CreateWithRovoPreviewRoute,
		navigation: false,
	},
	// Single catch-all for org; /o/:orgId/idp/:idpId is handled by org-routes path="idp/:idpId/*"
	// so the index route only runs for the true org root (/o/:orgId).
	'/o/:orgId/*': {
		importer: OrgRoutes,
		navigation: true,
	},
	// Use single catch-all for site so that pathname "remaining" after /s/:cloudId is non-empty for JSM paths
	// (e.g. .../idp/basicDirectoryId). Otherwise an index route in SiteRoutes would match and redirect to /users.
	'/s/:cloudId/*': {
		importer: SiteRoutes,
		navigation: true,
	},
	'/': {
		importer: Dashboard,
		navigation: true,
		exact: true,
		name: RouteNames.TOP_ROUTE_DASHBOARD,
	},
	'/cloud-first-create-a-migration/:orgId?': {
		importer: OrgOptIn,
		navigation: true,
	},
	'*': {
		importer: Error404Page,
		navigation: true,
	},
};

if (typeof window !== 'undefined' && '__ap__' in window) {
	ROOT_ROUTES['/adminhub'] = ROOT_ROUTES['/'];
}
